import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, RefreshCw, User } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COUNTRIES = [
  'India', 'United States', 'United Kingdom', 'Canada', 'Australia',
  'United Arab Emirates', 'Saudi Arabia', 'Singapore', 'Thailand', 'Malaysia'
];

const MyPassengers = () => {
  const { isAuthenticated } = useAuth();
  const [passengers, setPassengers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(null);
  
  const [formData, setFormData] = useState({
    title: 'Mr',
    first_name: '',
    last_name: '',
    gender: 'Male',
    dob: '',
    passport_no: '',
    passport_expiry: '',
    nationality: 'IN'
  });

  const fetchPassengers = useCallback(async () => {
    if (!isAuthenticated) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/user/passengers`, {
        withCredentials: true,
      });
      setPassengers(response.data.passengers);
    } catch (error) {
      console.error('Error fetching passengers:', error);
      toast.error('Failed to fetch passengers');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchPassengers();
  }, [fetchPassengers]);

  const resetForm = () => {
    setFormData({
      title: 'Mr',
      first_name: '',
      last_name: '',
      gender: 'Male',
      dob: '',
      passport_no: '',
      passport_expiry: '',
      nationality: 'IN'
    });
  };

  const createPassenger = async () => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/user/passengers`,
        null,
        {
          withCredentials: true,
          params: formData
        }
      );
      
      toast.success('Passenger added successfully');
      setCreateModal(false);
      resetForm();
      fetchPassengers();
    } catch (error) {
      console.error('Error creating passenger:', error);
      toast.error(error.response?.data?.detail || 'Failed to add passenger');
    }
  };

  const updatePassenger = async () => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/user/passengers/${editModal.id}`,
        null,
        {
          withCredentials: true,
          params: formData
        }
      );
      
      toast.success('Passenger updated successfully');
      setEditModal(null);
      resetForm();
      fetchPassengers();
    } catch (error) {
      console.error('Error updating passenger:', error);
      toast.error('Failed to update passenger');
    }
  };

  const deletePassenger = async (passengerId) => {
    if (!window.confirm('Are you sure you want to delete this passenger?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/user/passengers/${passengerId}`, {
        withCredentials: true,
      });
      
      toast.success('Passenger deleted successfully');
      fetchPassengers();
    } catch (error) {
      console.error('Error deleting passenger:', error);
      toast.error('Failed to delete passenger');
    }
  };

  const openEditModal = (passenger) => {
    setEditModal(passenger);
    setFormData({
      title: passenger.title === 'Miss' ? 'Ms' : (passenger.title || 'Mr'),
      first_name: passenger.first_name,
      last_name: passenger.last_name,
      gender: passenger.gender || 'Male',
      dob: passenger.dob || '',
      passport_no: passenger.passport_no || '',
      passport_expiry: passenger.passport_expiry || '',
      nationality: passenger.nationality
    });
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">My Passengers</h1>
          <p className="text-gray-500 text-sm">Save passenger details for quick booking</p>
        </div>
        <button
          onClick={() => { resetForm(); setCreateModal(true); }}
          className="px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Add Passenger
        </button>
      </div>

      {/* Passengers List */}
      <div className="bg-white rounded-xl border border-gray-200">
        {loading ? (
          <div className="p-12 text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-gray-400" />
            <p className="text-gray-500">Loading passengers...</p>
          </div>
        ) : passengers.length === 0 ? (
          <div className="p-12 text-center">
            <User className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500 mb-4">No passengers added yet</p>
            <button
              onClick={() => { resetForm(); setCreateModal(true); }}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Add Your First Passenger
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {passengers.map((passenger) => (
              <div key={passenger.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#1B375B] to-[#659A9B] rounded-full flex items-center justify-center">
                        <User className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 text-lg">
                          {(passenger.title === 'Miss' ? 'Ms' : passenger.title)} {passenger.first_name} {passenger.last_name}
                        </h3>
                        <p className="text-sm text-gray-500">{passenger.gender} • {passenger.nationality}</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                      {passenger.dob && (
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Date of Birth</p>
                          <p className="font-semibold text-gray-900">
                            {new Date(passenger.dob).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                      {passenger.passport_no && (
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Passport Number</p>
                          <p className="font-semibold text-gray-900">{passenger.passport_no}</p>
                        </div>
                      )}
                      {passenger.passport_expiry && (
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Passport Expiry</p>
                          <p className="font-semibold text-gray-900">
                            {new Date(passenger.passport_expiry).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => openEditModal(passenger)}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Edit Passenger"
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => deletePassenger(passenger.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete Passenger"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {createModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setCreateModal(false)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Add Passenger</h2>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Title *</label>
                  <select
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#659A9B]"
                  >
                    <option value="Mr">Mr</option>
                    <option value="Mrs">Mrs</option>
                    <option value="Ms">Ms</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Gender *</label>
                  <select
                    value={formData.gender}
                    onChange={(e) => setFormData({...formData, gender: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#659A9B]"
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">First Name *</label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#659A9B]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name *</label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#659A9B]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Date of Birth</label>
                  <input
                    type="date"
                    value={formData.dob}
                    onChange={(e) => setFormData({...formData, dob: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#659A9B]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Nationality *</label>
                  <select
                    value={formData.nationality}
                    onChange={(e) => setFormData({...formData, nationality: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#659A9B]"
                  >
                    <option value="IN">India</option>
                    <option value="US">United States</option>
                    <option value="GB">United Kingdom</option>
                    <option value="CA">Canada</option>
                    <option value="AU">Australia</option>
                    <option value="AE">United Arab Emirates</option>
                    <option value="SA">Saudi Arabia</option>
                    <option value="SG">Singapore</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Passport Number</label>
                  <input
                    type="text"
                    value={formData.passport_no}
                    onChange={(e) => setFormData({...formData, passport_no: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#659A9B]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Passport Expiry</label>
                  <input
                    type="date"
                    value={formData.passport_expiry}
                    onChange={(e) => setFormData({...formData, passport_expiry: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#659A9B]"
                  />
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => { setCreateModal(false); resetForm(); }}
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={createPassenger}
                className="flex-1 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold"
              >
                Add Passenger
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal (same as create) */}
      {editModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setEditModal(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Edit Passenger</h2>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">First Name</label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name</label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Nationality</label>
                <select
                  value={formData.nationality}
                  onChange={(e) => setFormData({...formData, nationality: e.target.value})}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  {COUNTRIES.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Passport Number</label>
                <input
                  type="text"
                  value={formData.passport_no}
                  onChange={(e) => setFormData({...formData, passport_no: e.target.value})}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Date of Issue</label>
                  <input
                    type="date"
                    value={formData.date_of_issue}
                    onChange={(e) => setFormData({...formData, date_of_issue: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Date of Expiry</label>
                  <input
                    type="date"
                    value={formData.date_of_expiry}
                    onChange={(e) => setFormData({...formData, date_of_expiry: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => { setEditModal(null); resetForm(); }}
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={updatePassenger}
                className="flex-1 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyPassengers;
